import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Brain, Zap, Target, Activity } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AnimatedBar = ({ height, delay, index }) => {
  const [currentHeight, setCurrentHeight] = useState(height);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeight(Math.random() * 60 + 40);
    }, 1000 + index * 200);
    return () => clearInterval(interval);
  }, [index]);

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: `${currentHeight}%` }}
      transition={{ 
        duration: 0.8,
        delay,
        ease: "easeInOut"
      }}
      className="w-full rounded-t-sm"
      style={{ 
        background: 'linear-gradient(180deg, #FF003C 0%, #FF003C80 100%)',
        boxShadow: '0 0 20px rgba(255, 0, 60, 0.5)'
      }}
    />
  );
};

const StatCard = ({ value, label, icon: Icon, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ borderColor: 'rgba(255, 0, 60, 0.5)' }}
      className="p-6 bg-[#050505] border border-white/10 rounded-sm transition-all duration-300"
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-[#FF003C]" />
        <span className="text-3xl font-bold text-white">{value}</span>
      </div>
      <span className="text-sm text-[#A3A3A3] font-mono tracking-wider">{label}</span>
    </motion.div>
  );
};

const PredictiveSection = () => {
  const { t } = useLanguage();
  const barCount = 24;

  const features = [
    { icon: Brain, text: t('predictive.features.ml') },
    { icon: Zap, text: t('predictive.features.realtime') },
    { icon: Target, text: t('predictive.features.accuracy') },
    { icon: Activity, text: t('predictive.features.processing') }
  ];

  return (
    <section 
      id="predictive" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="predictive-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <motion.span 
            className="font-mono text-sm tracking-[0.3em] uppercase"
            style={{ color: '#FF003C' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('predictive.subtitle')}
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 tracking-tight">
            {t('predictive.title')}
          </h2>
          <p className="text-[#A3A3A3] text-lg mt-6 max-w-2xl leading-relaxed">
            {t('predictive.description')}
          </p>
        </motion.div>

        {/* Animated Stats Chart - CRIMSON RED BARS */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 p-8 bg-[#050505] border border-white/10 rounded-sm"
          data-testid="stats-chart"
        >
          <div className="h-64 flex items-end justify-between gap-2">
            {[...Array(barCount)].map((_, index) => (
              <div key={index} className="flex-1 h-full flex items-end">
                <AnimatedBar 
                  height={Math.random() * 60 + 40} 
                  delay={index * 0.05}
                  index={index}
                />
              </div>
            ))}
          </div>
          
          {/* X-Axis Labels */}
          <div className="flex justify-between mt-4 text-xs font-mono text-[#A3A3A3]">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#FF003C' }} />
              <span className="text-sm text-[#A3A3A3]">Real-time Predictions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm border border-[#FF003C]/50" />
              <span className="text-sm text-[#A3A3A3]">Confidence Interval</span>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <StatCard 
            value="97.3%" 
            label={t('predictive.stats.accuracy')} 
            icon={Target}
            delay={0.1}
          />
          <StatCard 
            value="<5ms" 
            label={t('predictive.stats.latency')} 
            icon={Zap}
            delay={0.2}
          />
          <StatCard 
            value="847" 
            label={t('predictive.stats.models')} 
            icon={Brain}
            delay={0.3}
          />
          <StatCard 
            value="2.4B" 
            label={t('predictive.stats.datapoints')} 
            icon={Activity}
            delay={0.4}
          />
        </div>

        {/* Features List */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="flex items-center gap-4 p-4 border border-white/5 rounded-sm hover:border-[#FF003C]/30 transition-colors duration-300"
            >
              <feature.icon className="w-6 h-6 flex-shrink-0" style={{ color: '#FF003C' }} />
              <span className="text-white">{feature.text}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Ambient Glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #FF003C 0%, transparent 70%)'
        }}
      />
    </section>
  );
};

export default PredictiveSection;
